import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"

import type {
  ChatSessionParticipant,
  ChatSessionParticipantRole,
  ChatSessionParticipantsResponse,
  MessageVisibilityMode,
} from "@/api/models"

import { queryKeys } from "@/lib/query-keys"
import { SessionSharingService } from "@/services/session-sharing-service"

export function useSessionParticipants(sessionId: string | undefined, enabled: boolean = true) {
  return useQuery({
    queryKey: queryKeys.sessionParticipants(sessionId ?? ""),
    queryFn: () => SessionSharingService.listParticipants(sessionId!),
    enabled: enabled && !!sessionId,
    staleTime: 1000 * 60 * 2, // Consider data fresh for 2 minutes
    gcTime: 1000 * 60 * 5, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false, // Prevent refetch on tab switch
  })
}

export function useAddSessionParticipant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      sessionId,
      participant,
    }: {
      sessionId: string
      participant: {
        oid: string
        displayName: string
        type: "user" | "group"
        role: ChatSessionParticipantRole
        messageAccessMode: MessageVisibilityMode
        sharedFromMessageId?: string
      }
    }) => SessionSharingService.addParticipant(sessionId, participant),

    onMutate: async ({ sessionId, participant }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.sessionParticipants(sessionId),
      })

      const previousData = queryClient.getQueryData<ChatSessionParticipantsResponse>(
        queryKeys.sessionParticipants(sessionId)
      )

      if (previousData) {
        const newParticipant: ChatSessionParticipant = {
          ...participant,
          isOriginalOwner: false,
        }
        queryClient.setQueryData<ChatSessionParticipantsResponse>(
          queryKeys.sessionParticipants(sessionId),
          {
            ...previousData,
            participants: [...previousData.participants, newParticipant],
          }
        )
      }

      return { previousData }
    },

    onError: (_error, { sessionId }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.sessionParticipants(sessionId), context.previousData)
      }
      toast.error("Failed to add participant")
    },

    onSuccess: (_data, { sessionId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sessionParticipants(sessionId),
      })
      void queryClient.invalidateQueries({
        queryKey: ["session-history"],
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.fullSession(sessionId),
      })
      toast.success("Participant added")
    },
  })
}

export function useRemoveSessionParticipant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId, participantOid }: { sessionId: string; participantOid: string }) =>
      SessionSharingService.removeParticipant(sessionId, participantOid),

    onMutate: async ({ sessionId, participantOid }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.sessionParticipants(sessionId),
      })

      const previousData = queryClient.getQueryData<ChatSessionParticipantsResponse>(
        queryKeys.sessionParticipants(sessionId)
      )

      if (previousData) {
        queryClient.setQueryData<ChatSessionParticipantsResponse>(
          queryKeys.sessionParticipants(sessionId),
          {
            ...previousData,
            participants: previousData.participants.filter((p) => p.oid !== participantOid),
          }
        )
      }

      return { previousData }
    },

    onError: (_error, { sessionId }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.sessionParticipants(sessionId), context.previousData)
      }
      toast.error("Failed to remove participant")
    },

    onSuccess: (_data, { sessionId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sessionParticipants(sessionId),
      })
      void queryClient.invalidateQueries({
        queryKey: ["session-history"],
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.fullSession(sessionId),
      })
      toast.success("Participant removed")
    },
  })
}

export function useLeaveSharedSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId }: { sessionId: string }) =>
      SessionSharingService.leaveSharedSession(sessionId),

    onSuccess: (_data, { sessionId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sessionParticipants(sessionId),
      })
      void queryClient.invalidateQueries({
        queryKey: ["session-history"],
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sharedSessions(),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.fullSession(sessionId),
      })
    },
  })
}

export function useSharedSessions(sessionType?: string) {
  return useQuery({
    queryKey: queryKeys.sharedSessions(sessionType),
    queryFn: () => SessionSharingService.listSharedSessions(sessionType),
  })
}

export function useMarkSessionNotificationViewed() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (sessionId: string) => SessionSharingService.markNotificationViewed(sessionId),

    onSuccess: () => {
      // Invalidate session history queries to refresh notification indicators
      void queryClient.invalidateQueries({
        queryKey: ["session-history"],
      })
      // Also invalidate shared sessions query
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sharedSessions(),
      })
    },

    onError: () => {
      toast.error("Failed to mark notification as viewed")
    },
  })
}

export function useUpdateParticipantAccessMode() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      sessionId,
      participantOid,
      messageAccessMode,
      sharedFromMessageId,
    }: {
      sessionId: string
      participantOid: string
      messageAccessMode: MessageVisibilityMode
      sharedFromMessageId?: string
    }) =>
      SessionSharingService.updateParticipantAccessMode({
        sessionId,
        participantOid,
        messageAccessMode,
        sharedFromMessageId,
      }),

    onMutate: async ({ sessionId, participantOid, messageAccessMode, sharedFromMessageId }) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.sessionParticipants(sessionId),
      })

      const previousData = queryClient.getQueryData<ChatSessionParticipantsResponse>(
        queryKeys.sessionParticipants(sessionId)
      )

      if (previousData) {
        queryClient.setQueryData<ChatSessionParticipantsResponse>(
          queryKeys.sessionParticipants(sessionId),
          {
            ...previousData,
            participants: previousData.participants.map((p) =>
              p.oid === participantOid
                ? {
                    ...p,
                    messageAccessMode,
                    sharedFromMessageId,
                    sharedAt: new Date().toISOString(),
                  }
                : p
            ),
          }
        )
      }

      return { previousData }
    },

    onError: (_error, { sessionId }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.sessionParticipants(sessionId), context.previousData)
      }
      toast.error("Failed to update participant access mode")
    },

    onSuccess: (_data, { sessionId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sessionParticipants(sessionId),
      })
      void queryClient.invalidateQueries({
        queryKey: ["session-history"],
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.fullSession(sessionId),
      })
      toast.success("Access mode updated")
    },
  })
}

export function useTransferSessionOwnership() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ sessionId, newOwnerOid }: { sessionId: string; newOwnerOid: string }) =>
      SessionSharingService.transferOwnership(sessionId, newOwnerOid),

    onSuccess: (_data, { sessionId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sessionParticipants(sessionId),
      })
      // Ownership affects session lists (owned vs shared) and currentUserRole
      void queryClient.invalidateQueries({
        queryKey: ["session-history"],
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.sharedSessions(),
      })
      void queryClient.invalidateQueries({
        queryKey: queryKeys.fullSession(sessionId),
      })

      toast.success("Ownership transferred successfully", {
        description: "You are now a reader of this chat.",
      })
    },

    onError: (error: Error) => {
      toast.error("Failed to transfer ownership", {
        description: error.message || "An error occurred",
      })
    },
  })
}
